import type { AuthedRequester } from "@/lib/brokerAuth";
import type { BondBidResult, BondSubmission } from "@/lib/brokerBonds";
import { NAME_NOT_ON_FILE, type GiaSigner } from "@/lib/brokerGia";

// BRK-S331 — "Request letter from client": the Bid Spread Letter sent to the
// contractor for signature (DocuSign), consuming PMS-S724.
//
// Plan rule 23: required when the next-closest bid is more than 10 % above the
// final contract price; sent from the won-bid screen; flagged to the
// underwriter; NEVER blocks Submit. The offline upload slot stays — either path
// satisfies the readiness flag upstream.
//
// The order view's `spreadLetter` block and the send route were typed by hand
// to the shapes agreed with main on 2026-10-07; the sprint-10 release re-pin
// publishes both as written (the block REQUIRED and nullable on the order).

export const SPREAD_LETTER_STATUSES = [
  "not_sent",
  "sent",
  "signed",
  "uploaded",
] as const;
export type SpreadLetterStatus = (typeof SPREAD_LETTER_STATUSES)[number];

/** The order view's block — open on the wire, narrowed by `readSpreadLetter`. */
export interface BondSpreadLetterWire {
  required?: boolean;
  status?: string;
  sentAt?: string | null;
  signedAt?: string | null;
  documentId?: string | null;
  /** Underwriter-side (whether the envelope's figures match the saved bid
   *  result). Read tolerantly and deliberately rendered NOWHERE here. */
  figuresMatchBidResult?: boolean | null;
}

export interface SpreadLetterView {
  required: boolean;
  /** `null` for a status this build has not adopted — never actionable. */
  status: SpreadLetterStatus | null;
  sentAt: string | null;
  signedAt: string | null;
  documentId: string | null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/** The block, read — or `null` when PMS sent none (render nothing). */
export function readSpreadLetter(raw: unknown): SpreadLetterView | null {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }
  const row = raw as Record<string, unknown>;
  return {
    required: row.required === true,
    status: (SPREAD_LETTER_STATUSES as readonly unknown[]).includes(row.status)
      ? (row.status as SpreadLetterStatus)
      : null,
    sentAt: text(row.sentAt),
    signedAt: text(row.signedAt),
    documentId: text(row.documentId),
  };
}

const LABELS: Record<SpreadLetterStatus, string> = {
  not_sent: "Not sent",
  sent: "Sent for signature",
  signed: "Signed",
  uploaded: "Uploaded",
};

/** The chip's words. An unknown status says so, never a blank. */
export function spreadLetterStatusLabel(
  status: SpreadLetterStatus | null,
): string {
  return status === null ? "Status unavailable" : LABELS[status];
}

/**
 * Whether "Request letter from client" may be offered.
 *
 * On the won-bid screen — its one home since BRK-S502 — the NOTICE decides
 * (the figures are on screen, not yet saved), so an absent block or
 * `not_sent` offers it. Anything already sent, signed or uploaded — or a
 * status this build cannot read — offers nothing.
 */
export function offersSpreadLetterSend(view: SpreadLetterView | null): boolean {
  return view === null || view.status === "not_sent";
}

/** Plain shape check before the round trip; PMS validates the address too. */
export function isPlausibleEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** The two figures the letter is filled from. Plain numbers (D-10). */
export interface SpreadLetterFigures {
  finalContractPrice?: number;
  nextClosestBidAmount?: number;
}

const LETTER_FIGURES = ["finalContractPrice", "nextClosestBidAmount"] as const;

/**
 * Has the broker changed a letter figure on screen since the won result was
 * recorded?
 *
 * PMS fills the letter from the RECORDED won result, so a send with unsaved
 * figures would sign the contractor up to numbers the screen no longer shows
 * (PMS refuses it: 409 `SPREAD_LETTER_FIGURES_UNSAVED`). The won-bid screen
 * saves first when this is true. False before any win is recorded: the
 * figures then travel in the send's body, which is the only place they are.
 */
export function wonFiguresUnsaved(
  saved: BondBidResult | null | undefined,
  figures: SpreadLetterFigures,
): boolean {
  if (saved?.outcome !== "won") return false;
  return LETTER_FIGURES.some(
    (key) => (figures[key] ?? null) !== (saved[key] ?? null),
  );
}

export interface SpreadLetterSendInput extends SpreadLetterFigures {
  email: string;
  /** The signer's own name (PMS-S843): the indemnitor picked or the person
   *  typed. The envelope names this person rather than the account. */
  name: string;
}

/** One person the letter can go to: an indemnitor who has an email. */
export interface SpreadLetterRecipient {
  id: string;
  name: string;
  email: string;
}

/**
 * The indemnitors the broker can pick (BRK-S421): the order's GIA signers
 * that have an email and a name, in the GIA's order. One without an email
 * cannot be sent to, and one the roster names nobody for would put "Name not
 * on file" on the envelope, so both are left out; "Someone else" covers them.
 */
export function spreadLetterRecipients(
  signers: readonly GiaSigner[],
): SpreadLetterRecipient[] {
  return signers.flatMap((s) =>
    s.email && s.name.trim() && s.name !== NAME_NOT_ON_FILE
      ? [{ id: s.ownerId, name: s.name.trim(), email: s.email }]
      : [],
  );
}

/**
 * POST /api/broker/bond/submissions/{id}/spread-letter/send — the envelope to
 * the contractor. The figures are the screen's: PMS uses them while no won
 * result is recorded and otherwise fills the letter from the recorded one,
 * refusing figures that differ from it (409 `SPREAD_LETTER_FIGURES_UNSAVED`,
 * `details.fields`). 2xx answers the flat order view carrying `spreadLetter`;
 * that 409, `SPREAD_LETTER_NOT_REQUIRED` / `SPREAD_LETTER_TEMPLATE_UNAVAILABLE`
 * / `SPREAD_LETTER_ALREADY_OPEN` and a 422 naming a missing figure render
 * their sentence inline. Pinned (sprint-10 release).
 */
export function sendSpreadLetter(
  request: AuthedRequester,
  orderId: string,
  input: SpreadLetterSendInput,
): Promise<BondSubmission> {
  return request<BondSubmission>(
    `/api/broker/bond/submissions/${encodeURIComponent(orderId)}/spread-letter/send`,
    { method: "POST", body: input },
  );
}
